import { z } from 'zod';
import { canaryDeliverySchema, checkStatusSchema } from './check.js';
import { formSelectorsSchema } from './form.js';

/**
 * A qc_run_id looks like `qc_20260819_0214_hta3`: date, UTC time, client slug and a
 * short suffix. It is embedded in the canary's email address, which is what makes
 * correlation exact rather than time-window guesswork.
 *
 * @since 1.0.0
 */
export const QC_RUN_ID_PATTERN = /^qc_\d{8}_\d{4}_[a-z0-9]+$/;

export const qcRunIdSchema = z.string().regex(QC_RUN_ID_PATTERN, 'Malformed qc_run_id');

/**
 * One form the canary should exercise, as handed to the Playwright spec at run time
 * so adding a form never means editing the spec.
 *
 * @since 1.0.0
 */
export const canaryFormConfigSchema = z.object({
    formId: z.number().int(),
    formSlug: z.string(),
    formName: z.string(),
    pageUrl: z.url(),
    selectors: formSelectorsSchema,
    thresholdMs: z.number().int(),
});

export type CanaryFormConfig = z.infer<typeof canaryFormConfigSchema>;

/**
 * The oldest site-side plugin release that understands a client-delivery token. Older releases reject it,
 * so the dashboard never asks them to send a test to the client.
 *
 * @since 1.4.0
 */
export const CLIENT_DELIVERY_MIN_PLUGIN_VERSION = '1.2.0';

/**
 * Reads the numeric major, minor and patch out of a version string such as `1.2.0` or `v1.2.0-beta`.
 *
 * @since 1.4.0
 * @param version - the version string
 * @returns the three numbers, or null when the string is not a version
 */
export function parsePluginVersion(version: string | null | undefined): [number, number, number] | null {
    const match = version?.trim().match(/^v?(\d+)(?:\.(\d+))?(?:\.(\d+))?(?:[-+].*)?$/);
    if (!match) {
        return null;
    }

    return [Number(match[1]), Number(match[2] ?? 0), Number(match[3] ?? 0)];
}

/**
 * Reports whether a plugin version is at least a minimum, comparing each part as a number.
 *
 * @since 1.4.0
 * @param version - the version the site reported, or null when it is not known
 * @param minimum - the lowest acceptable version
 * @returns whether the version is known and not older than the minimum
 */
export function isPluginVersionAtLeast(version: string | null | undefined, minimum: string): boolean {
    const actual = parsePluginVersion(version);
    const required = parsePluginVersion(minimum);
    if (actual === null || required === null) {
        return false;
    }

    for (let index = 0; index < required.length; index += 1) {
        if (actual[index] !== required[index]) {
            return (actual[index] ?? 0) > (required[index] ?? 0);
        }
    }
    return true;
}

/**
 * An on-demand request the runner claimed for this run. Its id travels back with every run the runner
 * starts, so the dashboard knows the run was asked for and how its notification is delivered.
 *
 * @since 1.4.0
 */
export const canaryRequestSchema = z.object({
    id: z.number().int().positive(),
    delivery: canaryDeliverySchema,
    formId: z.number().int().nullable(),
});

export type CanaryRequest = z.infer<typeof canaryRequestSchema>;

/**
 * What the runner is told for one run: the forms, and every timeout it uses so the budget has one source.
 *
 * @since 1.0.0
 */
export const canaryConfigResponseSchema = z.object({
    clientSlug: z.string(),
    clientName: z.string(),
    forms: z.array(canaryFormConfigSchema),
    request: canaryRequestSchema.nullable().default(null),
    pollTimeoutMs: z.number().int().nonnegative(),
    runBudgetMs: z.number().int().nonnegative(),
    pageBudgetMs: z.number().int().nonnegative(),
    navigationTimeoutMs: z.number().int().nonnegative(),
    visibilityTimeoutMs: z.number().int().nonnegative(),
    actionTimeoutMs: z.number().int().nonnegative(),
    successTimeoutMs: z.number().int().nonnegative(),
    /** What the runner types into text fields, so the entry is recognisable as a test on the site. */
    testName: z.string().min(1),
    /** What the runner types into textarea fields. */
    testMessage: z.string().min(1),
    /** The hidden field the runner adds so the site's QC mode recognises the submission. */
    qcModeFieldName: z.string().min(1),
});

export type CanaryConfigResponse = z.infer<typeof canaryConfigResponseSchema>;

/**
 * What the runner sends to open one run. `requestId` names the on-demand request the run serves; a run
 * without one is a scheduled run.
 *
 * @since 1.0.0
 */
export const startCanaryRunRequestSchema = z.object({
    clientSlug: z.string(),
    formSlug: z.string(),
    requestId: z.number().int().positive().optional(),
});

export type StartCanaryRunRequest = z.infer<typeof startCanaryRunRequestSchema>;

/**
 * Everything the canary needs for one submission: the run identity, the address to
 * type into the form, and the signed token that puts the site's QC mode into effect
 * for this single submission.
 *
 * @since 1.0.0
 */
export const startCanaryRunResponseSchema = z.object({
    qcRunId: qcRunIdSchema,
    qcEmail: z.email(),
    qcModeToken: z.string(),
    thresholdMs: z.number().int(),
});

export type StartCanaryRunResponse = z.infer<typeof startCanaryRunResponseSchema>;

export const completeCanaryRunRequestSchema = z.object({
    isSubmitted: z.boolean(),
    hasSuccessMessage: z.boolean(),
    waitedMs: z.number().int().min(0),
    error: z.string().max(500).nullable().default(null),
    blockedReason: z.enum(['captcha', 'selector-missing', 'navigation', 'none']).default('none'),
});

export type CompleteCanaryRunRequest = z.infer<typeof completeCanaryRunRequestSchema>;

/**
 * Polled by the canary while it waits for the notification to show up in Mailgun.
 *
 * @since 1.0.0
 */
export const canaryRunStatusSchema = z.object({
    qcRunId: qcRunIdSchema,
    isMailConfirmed: z.boolean(),
    correlationMs: z.number().int().nullable(),
    status: checkStatusSchema,
});

export type CanaryRunStatus = z.infer<typeof canaryRunStatusSchema>;

export const runCanaryRequestSchema = z.object({
    clientSlug: z.string().optional(),
    formId: z.number().int().optional(),
});

export type RunCanaryRequest = z.infer<typeof runCanaryRequestSchema>;

/**
 * What a canary request queued and what it actually started, for one form or for
 * every client. `isMailboxVerified` and `isChecklyConfigured` explain a run that queued
 * but never began.
 *
 * @since 1.2.0
 */
export const canaryRunResultSchema = z.object({
    queued: z.number().int().nonnegative(),
    started: z.number().int().nonnegative(),
    isMailboxVerified: z.boolean(),
    isChecklyConfigured: z.boolean(),
});

export type CanaryRunResult = z.infer<typeof canaryRunResultSchema>;
